<template>
    <transition name="fade">
        <div
            v-if="isOpen"
            class="d-modal"
        >
            <section
                class="modal-box"
                :class="{'big': isBig, 'hasMessage': !!message, 'hasSlot': hasSlot, 'small': isSmall, 'medium': isMedium }"
            >
                <header
                    :class="modalType"
                    class="d-flex title"
                >
                    <DIcon
                        v-if="isError"
                        name="alert"
                    />
                    <DIcon
                        v-if="isSuccess"
                        name="check"
                    />
                    <DIcon
                        v-if="isWarning"
                        name="account-question"
                    />
                    <DIcon
                        v-if="modalType.isInfo"
                        name="information-slab-circle-outline"
                    />
                    <h1>{{ title }}</h1>
                    <slot name="header" />
                    <DButton
                        class="ms-auto"
                        icon="close"
                        mini
                        @click="isConfirm ? dismiss() : close(true)"
                    />
                </header>

                <p v-if="message">
                    {{ message }}
                </p>
                <p
                    v-if="error"
                    class="pt-0 font-italic ms-3"
                >
                    {{ error }}
                </p>
                <p
                    v-if="errorTrace.length > 1"
                    class="pt-0"
                >
                    {{ __('messages.full_error_trace') }}
                </p>
                <div v-if="errorTrace.length > 1">
                    <p
                        v-for="(trace, key) in errorTrace"
                        :key="key"
                        class="pt-0 font-italic ms-3"
                    >
                        {{ trace }}
                    </p>
                </div>
                <p
                    v-if="linkMessage"
                    class="pt-0"
                >
                    <span>{{ linkMessage }}</span>
                    <Link
                        v-if="link"
                        :href="link"
                    >
                        {{ link }}
                    </Link>
                </p>
                <main
                    v-if="hasSlot"
                    :class="overflowAuto ? 'overflow-auto' : ''"
                >
                    <slot />
                </main>

                <div
                    v-if="isConfirm"
                    class="actions"
                >
                    <div class="additional-data">
                        <DFormTextarea
                            v-if="showChangeReasonInput"
                            id="change_reason"
                            v-model="changeReason"
                            class="change-reason"
                            :placeholder="__('messages.change_reason')"
                            add-input-classes="bg-light"
                            :is-valid="isFormFieldValid('activity_log_comment')"
                            :invalid-feedback="propErrors.activity_log_comment"
                            maxlength="1000"
                            @update:modelValue="updateValue"
                        />
                        <div
                            v-if="showIncidentList === true && incidents.length > 1"
                            class="incident-class"
                        >
                            <span>{{ __('customers.business_incidents.add_to_incident') }}</span>
                            <DSelect
                                id="add_incident"
                                v-model="incidentId"
                                :options="incidents"
                                :is-valid="isFormFieldValid('incident_id')"
                                @change="updateIncident"
                            />
                        </div>
                        <p
                            v-if="propErrors.incident_id"
                            class="error-msg"
                        >
                            {{ propErrors.incident_id }}
                        </p>
                    </div>
                    <slot name="actions" />
                    <DButton
                        :label="__(confirmButtonText)"
                        :disabled="isInLoadingState || disabledAction"
                        :color="modalColor"
                        @click="confirm"
                    />
                    <!--
                        Deliberately not disabled by isInLoadingState. The header's X runs this same dismiss() and has
                        never been disabled, so disabling this one hid a control rather than preventing anything -- and
                        isInLoadingState is a global counter driven by every axios request in the app, so an unrelated
                        background call could disable a modal's own close button. See the header button above.
                    -->
                    <DButton
                        :label="__('messages.close')"
                        color="secondary"
                        @click="dismiss"
                    />
                </div>
                <div
                    v-if="!isConfirm"
                    class="actions"
                >
                    <div class="additional-data">
                        <DInput
                            v-if="showChangeReasonInput"
                            id="change_reason"
                            v-model="changeReason"
                            class="change-reason"
                            :placeholder="__('messages.change_reason')"
                            add-input-classes="bg-light"
                            :is-valid="isFormFieldValid('activity_log_comment')"
                            :invalid-feedback="propErrors.activity_log_comment"
                            maxlength="1000"
                            @update:value="updateValue"
                        />
                        <div
                            v-if="showIncidentList === true && incidents.length > 1 && userPermissions.includes('customer.incidents_view') && userPermissions.includes('customer.incidents_manage')"
                            class="incident-class"
                        >
                            <span>{{ __('customers.business_incidents.add_to_incident') }}</span>
                            <DSelect
                                id="add_incident"
                                v-model="incidentId"
                                :options="incidents"
                                :is-valid="isFormFieldValid('incident_id')"
                                @change="updateIncident"
                            />
                        </div>
                        <p
                            v-if="propErrors.incident_id"
                            class="error-msg"
                        >
                            {{ propErrors.incident_id }}
                        </p>
                    </div>
                    <slot name="actions" />
                    <!-- Not disabled by isInLoadingState, for the reason given on the confirm branch's close button. -->
                    <DButton
                        :label="__('messages.close')"
                        :color="modalColor"
                        @click="close(true)"
                    />
                    <DButton
                        v-if="showCloseConfirmation"
                        :label="confirmCloseText"
                        color="danger"
                        @click="close(true, true)"
                    />
                </div>
            </section>
        </div>
    </transition>
</template>

<script>

export default {
    name: 'DModal',
    props: {
        title: {
            type: String,
            required: true
        },
        message: {
            type: String,
            default: '',
            required: false
        },
        error: {
            type: String,
            default: '',
            required: false
        },
        linkMessage: {
            type: String,
            default: '',
            required: false
        },
        link: {
            type: String,
            default: '',
            required: false
        },
        isError: {
            type: Boolean,
            required: false,
            default: false
        },
        isSuccess: {
            type: Boolean,
            required: false,
            default: false
        },
        isWarning: {
            type: Boolean,
            required: false,
            default: false
        },
        isInfo: {
            type: Boolean,
            required: false,
            default: false
        },
        isConfirm: {
            type: Boolean,
            required: false,
            default: false
        },
        isBig: {
            type: Boolean,
            required: false,
            default: false
        },
        isSmall: {
            type: Boolean,
            required: false,
            default: false
        },
        isMedium: {
            type: Boolean,
            required: false,
            default: false
        },
        isAwaitingValidation: {
            type: Boolean,
            required: false,
            default: false
        },
        show: {
            type: Boolean,
            required: true,
            default: false
        },
        closeColorSecondary: {
            type: Boolean,
            required: false,
            default: false
        },
        showChangeReasonInput: {
            type: Boolean,
            default: false
        },
        confirmClose: {
            type: Boolean,
            default: false
        },
        confirmCloseText: {
            type: String,
            default: ''
        },
        overflowAuto: {
            type: Boolean,
            default: false
        },
        confirmButtonText: {
            type: String,
            default: 'messages.confirm'
        },
        showIncidentList: {
            type: Boolean,
            default: false
        },
        customerId: {
            type: Number,
            default: 0
        },
        disabledAction: {
            type: Boolean,
            default: false
        },
        errorTrace: {
            type: Array,
            default: () => { return [] }
        }
    },
    emits: ['confirm', 'dismiss', 'open', 'close', 'clearFields', 'update:modelValue', 'setIncident'],
    data () {
        return {
            isOpen: false,
            changeReason: '',
            showCloseConfirmation: false,
            incidents: [],
            incidentId: null,
            showErrorTrack: false
        }
    },
    computed: {
        hasSlot () {
            return Object.keys(this.$slots).length !== 0
        },
        propErrors () {
            return this.$page.props.errors
        },
        userPermissions () {
            return this.$page.props.auth.permissions
        },
        modalColor () {
            if (this.isSuccess) {
                return 'success'
            }
            if (this.isError) {
                return 'danger'
            }
            if (this.isWarning) {
                return 'warning'
            }
            if (this.isInfo) {
                return 'info'
            }
            return 'primary'
        },
        modalType () {
            return {
                isError: this.isError,
                isSuccess: this.isSuccess,
                isWarning: this.isWarning,
                isInfo: !this.isError && !this.isSuccess && !this.isWarning
            }
        }
    },
    watch: {
        show: {
            immediate: true,
            handler (show) {
                if (show) {
                    this.open()
                } else {
                    this.close()
                }
            }
        }
    },
    created () {
        window.addEventListener('keydown', this.handleKeys)

        if (this.showIncidentList === true && this.customerId !== 0 && this.userPermissions.includes('customer.incidents_view') && this.userPermissions.includes('customer.incidents_manage')) {
            window.axios.get(this.$route('customers.customers.get-customer-open-business-incidents-array', this.customerId))
                .then(response => {
                    this.incidents = response.data.incidents
                })
        }
    },
    unmounted () {
        window.removeEventListener('keydown', this.handleKeys)
    },
    methods: {
        confirm () {
            this.$emit('confirm')
            if (!this.isAwaitingValidation) {
                this.close(false)
            }
        },
        dismiss () {
            this.$emit('dismiss')
            this.changeReason = ''
            this.close(true)
        },
        open () {
            this.isOpen = true
            this.$emit('open')
        },
        close (clearFields = false, closeConfirmed = false) {
            if (this.confirmClose === true && closeConfirmed === false) {
                this.showCloseConfirmation = true

                return
            }

            this.isOpen = false
            this.$emit('close')
            this.changeReason = ''
            if (clearFields) {
                this.$emit('clearFields')
            }
        },
        handleKeys (event) {
            // ESC Key
            if (event.keyCode === 27) {
                if (this.isConfirm) {
                    this.dismiss()
                } else {
                    this.close(true)
                }
            }
        },
        updateValue () {
            this.$emit('update:modelValue', this.changeReason)
        },
        updateIncident () {
            this.$emit('setIncident', this.incidentId)
        }
    }
}
</script>

<style lang="scss" scoped>
.d-modal {
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(5px);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 5000;
    padding: 50px;
    pointer-events: all !important;

    & .modal-box {
        background: var(--colorAppSurface);
        // Column flex rather than grid, so the scrollable content can shrink while the header and the actions row
        // cannot. As a grid every child sized to its own content, and `overflow: hidden` then clipped whatever did not
        // fit — which is the actions row at the bottom. A tall modal (an apply with every section selected) scrolled
        // its body but put its own buttons permanently out of reach.
        display: flex;
        flex-direction: column;
        width: 90%;
        max-width: 520px;
        border-radius: 5px;
        max-height: 100%;
        overflow: hidden;

        & header {
            display: grid;
            grid-template-columns: 1fr auto;
            grid-gap: 15px;
            padding: 15px 20px;
            align-items: center;
            border-bottom: 1px solid var(--colorAppBorder);
            // The title and the close button stay put while the body scrolls under them.
            flex: 0 0 auto;

            &.title {
                grid-template-columns: auto 1fr auto;
                color: #fff;
            }

            &.isError {
                background: var(--colorDanger);
            }

            &.isSuccess {
                background: var(--colorSuccess);
            }

            &.isWarning {
                background: var(--colorWarningLight);
            }

            &.isInfo {
                background: var(--colorInfo);
                color: var(--cui-body-color-rgb);
            }

            & .mdi {
                opacity: 0.6;
            }
            & h1 {
                font-size: 18px;
                margin: 0;
            }
        }
        & p {
            padding: 25px 20px;
            line-height: 1.5em;
            margin: 0;
            opacity: 0.6;
        }
        & .actions {
            border-top: 1px solid var(--colorAppBorder);
            padding: 15px 20px;
            display: flex;
            justify-content: flex-end;
            align-items: center;
            grid-gap: 10px;
            // Never give up space to the scrolling content above: the buttons are the point of the modal.
            flex: 0 0 auto;
        }

        & main, & p {
            overflow-x: auto;
            overflow-y: auto;
            width: 100%;
            // Shrink to whatever the header and the actions row leave behind, rather than to a guess at their height.
            // The previous `max-height: calc(100vh - 230px)` hard-coded that guess, ignored the 50px padding on
            // .d-modal, and applied to every middle element separately — so a modal carrying both a message and a slot
            // could claim nearly twice the viewport between them. min-height: 0 is what allows a flex item to shrink
            // below its content size at all; without it the overflow rules above never get anything to scroll.
            min-height: 0;
            flex: 0 1 auto;
        }

        // With a slot present, the paragraphs DModal renders itself — the message, the error and the link line — never
        // give up space to it. flex-shrink distributes a deficit in proportion to each item's base size, so next to a
        // tall slot (a comparison result carrying hundreds of violations) a three-line message was shrunk to almost
        // nothing and handed a scrollbar of its own: two scrollbars, one of them on a paragraph with no reason to
        // scroll. The slot is the part that can afford to scroll, so it absorbs the whole deficit.
        //
        // Only direct children: paragraphs inside the slot are not flex items of this box, so the rule above never
        // applied to them anyway. And only with a slot — without one there is nothing to protect the message from, and
        // shrinking is then the right behaviour, because a long message is all the modal has to show.
        &.hasSlot > p {
            flex: 0 0 auto;
            // Backstop for the one case this gives up on: a message long enough to fill the box on its own would push
            // the actions row out of view again, which is the bug this flex layout exists to fix. Deliberately
            // generous — the messages above a slot are a sentence or two, so it should never engage.
            max-height: 40vh;
        }

        &.big {
            max-width: 1000px;
        }

        &.small {
            max-width: 300px;
        }

        &.medium {
            max-width: 700px;
        }

    }
    &.dropdown-overflow-visible{
        overflow: hidden;
        & main, & p {
            overflow-x: visible;
            overflow-y: visible;
        }
    }
}

.overflow-auto {
    overflow: auto !important;
}

.fade-enter-active, .fade-leave-active {
    transition: opacity .1s;
}
.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
    opacity: 0;
}
.additional-data {
    width: 100%;
}
.incident-class {
    padding-top: 10px !important;
    padding-right: 10px !important;
    display: grid !important;
    grid-template-columns: auto auto !important;
    grid-gap: 10px !important;
    align-items: center !important;
    width: 100% !important;
}
.error-msg {
    margin: 3px 0 0 75px;
    text-align: center;
    font-size: 12px;
    color: #ef4d4d;
}
.font-italic {
    font-style: italic;
}
</style>
